import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { notification, Alert, Table, Space } from 'antd'
import { UploadModal } from 'modules/core/components'
import { generateMessageError } from 'modules/core/utils'
import configuration from 'modules/configuration'
import { connect } from 'react-redux'
import moment from 'moment'

const flattenImportErrors = (errors = [], generalLabel) =>
  errors.flatMap(({ row, errors: rowErrors = {} }) =>
    Object.entries(rowErrors).flatMap(([column, messages = []]) =>
      messages.map((message) => ({
        row,
        column: column === 'general' ? generalLabel : column,
        message,
      })),
    ),
  )

const ImportStrategiesModal = ({
  visible,
  onCancel,
  onSuccessUpload,
  setIsTableLoading,
  importStrategies,
  downloadStrategiesImportTemplate,
}) => {
  const [isUploadingFile, setIsUploadingFile] = useState(false)
  const [fileList, setFileList] = useState([])
  const [importErrors, setImportErrors] = useState([])
  const [createdCount, setCreatedCount] = useState(0)
  const { t } = useTranslation()

  const resetResult = () => {
    setImportErrors([])
    setCreatedCount(0)
  }

  const onClose = () => {
    onCancel()
    setFileList([])
    setIsUploadingFile(false)
    resetResult()
  }

  const showResult = ({ created = 0, failed = 0, errors = [] }) => {
    // Las filas válidas se crean aunque otras fallen: refrescamos lista + KPIs.
    if (created > 0) onSuccessUpload()

    if (failed > 0) {
      // Importación parcial o total con errores: mantenemos el modal abierto
      // y mostramos el detalle por fila para que el usuario corrija y reintente.
      setCreatedCount(created)
      setImportErrors(flattenImportErrors(errors, t('CONFIG_APPROVALS_IMPORT_ERROR_GENERAL')))
      setFileList([])
    } else {
      notification.success({
        message: t('FEEDBACK_VERY_GOOD'),
        description: t('CONFIG_APPROVALS_IMPORT_SUCCESS_FEEDBACK', { created }),
        duration: 0,
      })
      onClose()
    }
    setIsUploadingFile(false)
    setIsTableLoading(false)
  }

  const onConfirmUpload = () => {
    setIsUploadingFile(true)
    setIsTableLoading(true)
    resetResult()
    importStrategies(fileList)
      .then((response) => showResult(response?.payload?.data || {}))
      .catch((error) => {
        // Defensivo: si el detalle por fila viene por un status de error, igual lo mostramos.
        const data = error?.response?.data
        if (data && Array.isArray(data.errors)) {
          showResult(data)
          return
        }
        notification.error({
          message: t('FEEDBACK_DEFAULT_ERROR'),
          description: generateMessageError(error),
          duration: 0,
        })
        setIsUploadingFile(false)
        setIsTableLoading(false)
      })
  }

  const onAddUploadFile = (file) => {
    setFileList([file])
    resetResult()
    return false
  }

  const errorColumns = [
    {
      title: t('CONFIG_APPROVALS_IMPORT_ERROR_COL_ROW'),
      dataIndex: 'row',
      key: 'row',
      width: 70,
      align: 'center',
    },
    {
      title: t('CONFIG_APPROVALS_IMPORT_ERROR_COL_COLUMN'),
      dataIndex: 'column',
      key: 'column',
      width: 140,
    },
    {
      title: t('CONFIG_APPROVALS_IMPORT_ERROR_COL_ERROR'),
      dataIndex: 'message',
      key: 'message',
    },
  ]

  const renderErrors = () =>
    importErrors.length > 0 && (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type="warning"
          showIcon
          message={
            createdCount > 0
              ? t('CONFIG_APPROVALS_IMPORT_ERROR_TITLE_PARTIAL', { created: createdCount })
              : t('CONFIG_APPROVALS_IMPORT_ERROR_TITLE_NONE')
          }
        />
        <Table
          size="small"
          columns={errorColumns}
          dataSource={importErrors.map((item, index) => ({ ...item, key: index }))}
          pagination={importErrors.length > 8 ? { pageSize: 8, size: 'small' } : false}
          scroll={{ y: 240 }}
        />
      </Space>
    )

  return (
    <UploadModal
      title={t('CONFIG_APPROVALS_IMPORT_MODAL_TITLE')}
      visible={visible}
      onCancel={onClose}
      onConfirm={onConfirmUpload}
      uploadProps={{
        name: 'file',
        accept: '.xls, .xlsx',
        showUploadList: { showPreviewIcon: false },
        beforeUpload: onAddUploadFile,
        onRemove: () => setFileList([]),
        fileList: fileList,
      }}
      isUploadingFile={isUploadingFile}
      extraBodyBottom={renderErrors()}
      downloadTemplateFile={() =>
        downloadStrategiesImportTemplate(
          t('CONFIG_APPROVALS_IMPORT_TEMPLATE_NAME', { date: moment().format('lll') }),
        )
      }
    />
  )
}

const mapDispatchToProps = {
  importStrategies: configuration.actions.importStrategies,
  downloadStrategiesImportTemplate: configuration.actions.downloadStrategiesImportTemplate,
}

export default connect(null, mapDispatchToProps)(ImportStrategiesModal)
